import React, { useCallback, useMemo } from 'react'
import { Alert, Card } from 'antd'
import { DiffOutlined } from '@ant-design/icons'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'hooks/useSearchParams'
import { listIdeonVersions, startIdeonCompare } from '../api/ideon-compare-api'
import { getCompareRun, type CompareRun } from '../api/compare-run-api'
import { useCompareRun } from '../hooks/useCompareRun'
import {
  defaultDataType,
  isSurecoCsvDataType,
  parseStatesParam,
  resolveSelection,
  toIdeonCompareResult,
  type IdeonCompareSelection,
} from '../utils/ideonCompareHelpers'
import type { SurecoCsvDataType } from '../constants'
import { extractErrorStatus } from 'routes/AbpUploadDashboard/utils/extract-error-message'
import { IdeonVersionSelector } from './IdeonVersionSelector'
import { IdeonCompareResults } from './IdeonCompareResults'
import { CompareRunProgress } from './CompareRunProgress'
import styles from '../PlanManagementV3.module.scss'

type IdeonCompareUrlParams = {
  year?: string
  base?: string
  target?: string
  /** runId of the result on screen. */
  result?: string
  type?: string
  /** Results filter: comma-separated state codes; absent = all states. */
  states?: string
  /** Results filter: HIOS plan / issuer ID prefix. */
  hios?: string
}

const QUERY_KEYS = {
  versions: ['ideonCompareVersions'],
  run: 'ideonCompareRun',
}

function selectionToParams(
  selection: IdeonCompareSelection
): Partial<IdeonCompareUrlParams> {
  return {
    year: selection.planYear?.toString(),
    base: selection.baseVersionId ?? undefined,
    target: selection.targetVersionId ?? undefined,
  }
}

function isNotFound(err: unknown): boolean {
  return extractErrorStatus(err) === 404
}

export function IdeonCompareTab() {
  const queryClient = useQueryClient()
  const { params, setParams } = useSearchParams<IdeonCompareUrlParams>()
  const states = useMemo(() => parseStatesParam(params.states), [params.states])

  const versionsQuery = useQuery({
    queryKey: QUERY_KEYS.versions,
    queryFn: listIdeonVersions,
  })
  const versions = useMemo(() => versionsQuery.data ?? [], [versionsQuery.data])

  const selection = useMemo(
    () => resolveSelection(params, versions),
    [params, versions]
  )

  const runQuery = useQuery({
    queryKey: [QUERY_KEYS.run, params.result ?? ''],
    queryFn: () => getCompareRun(params.result ?? ''),
    enabled: !!params.result,
    // A completed run never changes (fetches are immutable), so once loaded
    // it never needs refetching; a running or failed one opened from a link
    // still can.
    staleTime: (query) =>
      query.state.data?.status === 'COMPLETED' ? Infinity : 0,
    retry: (failureCount, err) => !isNotFound(err) && failureCount < 2,
  })
  const run = params.result ? (runQuery.data ?? null) : null
  const result = run ? toIdeonCompareResult(run, versions) : null
  const isRunMissing =
    !!params.result &&
    (isNotFound(runQuery.error) ||
      (run !== null &&
        versionsQuery.isSuccess &&
        run.status === 'COMPLETED' &&
        result === null))

  const dataType: SurecoCsvDataType = isSurecoCsvDataType(params.type)
    ? params.type
    : defaultDataType(result, states)

  // Also aligns the selectors with the run — after a refresh the resumed run
  // may not match whatever the URL selection was.
  const openRun = useCallback(
    (r: CompareRun) => {
      queryClient.setQueryData([QUERY_KEYS.run, r.runId], r)
      setParams(
        {
          year: r.year?.toString(),
          base: r.baseTaskId ?? undefined,
          target: r.targetTaskId ?? undefined,
          result: r.runId,
          type: undefined,
        },
        { replace: true }
      )
    },
    [queryClient, setParams]
  )

  const job = useCompareRun({ source: 'IDEON', onCompleted: openRun })

  const handleCompare = () => {
    if (!selection.baseVersionId || !selection.targetVersionId) return
    // The backend reuses a saved result for this pair when there is one, so
    // a repeat compare opens instantly without a job.
    const { baseVersionId, targetVersionId } = selection
    void job.start(() => startIdeonCompare({ baseVersionId, targetVersionId }))
  }

  const renderBody = () => {
    if (job.isRunning) {
      return (
        <CompareRunProgress
          phase={job.phase}
          progress={job.progress}
          stage={job.stage}
          description="Checking all 5 data types across every state. This can take a few minutes — you can leave this page and come back."
          canCancel={job.canCancel}
          onCancel={job.cancel}
        />
      )
    }
    if (isRunMissing) {
      return (
        <Alert
          type="warning"
          showIcon
          className={styles.compareAlert}
          message="This comparison is no longer available"
          description="One of its versions may have been deleted. Pick two fetches above and run a new compare."
          closable
          onClose={() =>
            setParams({ result: undefined, type: undefined }, { replace: true })
          }
        />
      )
    }
    if (run && run.status === 'FAILED') {
      return (
        <Alert
          type="error"
          showIcon
          className={styles.compareAlert}
          message="This comparison failed"
          description={run.errorMessage ?? 'Click Compare to run it again.'}
        />
      )
    }
    if (run && run.status === 'RUNNING') {
      return (
        <Alert
          type="info"
          showIcon
          className={styles.compareAlert}
          message="This comparison is still running"
          description="Click Compare to follow its progress."
        />
      )
    }
    if (result) {
      return (
        <IdeonCompareResults
          result={result}
          dataType={dataType}
          onDataTypeChange={(type) => setParams({ type }, { replace: true })}
          states={states}
          // Pins the type on screen: with no type in the URL it's derived
          // from the state filter and would jump to another tab.
          onStatesChange={(next) =>
            setParams(
              {
                states:
                  next.length > 0 ? [...next].sort().join(',') : undefined,
                type: dataType,
              },
              { replace: true }
            )
          }
          hiosSearch={params.hios ?? ''}
          onHiosSearchChange={(hios) =>
            setParams({ hios: hios || undefined }, { replace: true })
          }
        />
      )
    }
    if (params.result && (runQuery.isLoading || versionsQuery.isLoading)) {
      return null
    }
    return (
      <div className={styles.ideonNoDiff}>
        <div className={styles.emptyStateIcon}>
          <DiffOutlined />
        </div>
        <h2 className={styles.emptyStateTitle}>Compare two Ideon fetches</h2>
        <p className={styles.emptyStateDescription}>
          Pick two fetches above and click Compare to see what changed between
          them.
        </p>
      </div>
    )
  }

  return (
    <div className={styles.compareTabRoot}>
      <div className={`${styles.cardHeader} ${styles.compareHeader}`}>
        <h1 className={styles.cardHeaderTitle}>Compare Ideon</h1>
      </div>
      <Card>
        <IdeonVersionSelector
          versions={versions}
          isLoadingVersions={versionsQuery.isLoading}
          selection={selection}
          isRunning={job.isRunning}
          // Any selection change clears the result on screen, so a result
          // never sits next to selectors that no longer describe it. The
          // state and HIOS filters belong to the result, so they're kept.
          onChange={(next) =>
            setParams(
              {
                ...selectionToParams(next),
                result: undefined,
                type: undefined,
              },
              { replace: true }
            )
          }
          onCompare={handleCompare}
        />

        {versionsQuery.isError && (
          <Alert
            type="error"
            showIcon
            className={styles.compareAlert}
            message="Could not load Ideon fetches"
            description="Refresh the page to try again."
          />
        )}
        {job.error && (
          <Alert
            type="error"
            showIcon
            closable
            className={styles.compareAlert}
            message="Compare failed"
            description={job.error}
            onClose={job.clearError}
          />
        )}

        {renderBody()}
      </Card>
    </div>
  )
}
